import React, { useState, useRef, useEffect } from "react";
import { NoMessages } from "asset/icons";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { LuDot } from "react-icons/lu";
import { createOrderMessage, getMessageReplies } from "api/fetchers";
import getInitialsMessage from "utilities/messages/getInitialsMessage";
import formatDate from "utilities/messages/formatDate";
import InputSection from "component/flex/message/InputSection";
import { SOCKET_EVENTS, SOCKET_LISTENERS } from "constants/global";
import useSocketEvent from "hooks/useSocketEvent";
import UploadDocModal from "component/form/uploadDocModal";
import useUploadDoc from "hooks/useUploadDoc";
import { toast } from "react-toastify";
import { isEmpty, uniqBy } from "lodash";
import Loader from "component/loader";

const Messages = ({
    messages = [],
    onSendMessage,
    orderId,
    businessName,
    sendMessage,
    socketData,
    convoDetails,
}) => {
    const [message, setMessage] = useState("");
    const [visibleReplies, setVisibleReplies] = useState(null);
    const [repliesData, setRepliesData] = useState({});
    const [replyInputData, setReplyInputData] = useState({});
    const [inputId, setInputId] = useState("");
    const [loading, setLoading] = useState(false);
    const fileInputRefs = useRef({});
    const { socket } = socketData ?? {};
    const {
        addDocs: addDocModal,
        setAddDocs: setAddDocModal,
        handleUpload,
        handleFileRemove,
        fileData,
    } = useUploadDoc({
        shipmentId: orderId,
    });

    useEffect(() => {
        if (socket) { 
            setLoading(true);
            socket.emit(SOCKET_EVENTS.GET_MESSAGES, {
                conversationId: convoDetails?.conversationId,
            })
        }

        return () => { 
            socket.off(SOCKET_EVENTS.GET_MESSAGES);
        }
    }, [convoDetails?.conversationId]);

    useSocketEvent([
        {
            name: SOCKET_LISTENERS.ERROR,
            cb: (error) => {
                console.log("An error has occurred", { error });
            },
        },
        {
            name: SOCKET_LISTENERS.FETCH_MESSAGES,
            cb: (data) => {
                setLoading(false);
                onSendMessage([...JSON.parse(data)?.messages?.messages]);
            },
        },
        {
            name: SOCKET_LISTENERS.MESSAGE_SENT,
            cb: (data) => {
                setLoading(false);
                onSendMessage((prev) => [...prev, JSON.parse(data)?.message]);
            },
        }
    ]);

    const sendMessageFunc = async (messageId) => {
        // Ensure at least one of message or attachment is present
        if (message.trim() === "" && isEmpty(fileData)) {
            toast.error("Please enter a message or attach a file.");
            return;
        }
        const validId = typeof messageId === "string" && messageId.length > 0;
        const payload = {
            conversationId: convoDetails?.conversationId,
            message: !validId ? message : replyInputData[messageId],
        };
        if (fileData?.[inputId]?.url) {
            payload.attachmentUrls = fileData?.[inputId]?.url ? [fileData?.[inputId]?.url] : null;
        }
        if (validId) { 
            payload.parentMessageId = messageId;
        }

        try {
            setLoading(true);
            socket?.emit(SOCKET_EVENTS.SEND_MESSAGE, payload);
            setReplyInputData({});
            setMessage("");
            setVisibleReplies(null);
            handleFileRemove(inputId);
        } catch (error) {
            console.error("Error sending message:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        // Ensure at least one of message or attachment is present
        if (message.trim() === "" && !fileData["main"]) {
            return;
        }

        try {
            const data = sendMessage
                ? await sendMessage({ message, file: fileData["main"] })
                : await createOrderMessage(orderId, message, fileData["main"]);
            onSendMessage((prevMessages) => [...prevMessages, data]);

            setMessage("");
            handleFileRemove("main");
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const handleAttachClick = (messageId) => {
        if (visibleReplies !== messageId) {
            setVisibleReplies(messageId);

            // Use a short delay to ensure rendering completes before triggering the file input click
            setTimeout(() => {
                if (fileInputRefs.current[messageId]) {
                    fileInputRefs.current[messageId].click();
                } else {
                    console.error(
                        `File input reference for message ${messageId} is not available.`
                    );
                }
            }, 0);
        } else if (fileInputRefs.current[messageId]) {
            fileInputRefs.current[messageId].click();
        } else {
            console.error(`File input reference for message ${messageId} is not available.`);
        }
    };

    const fetchReplies = async (messageId) => {
        try {
            const data = await getMessageReplies(messageId);
            setRepliesData((prevReplies) => ({ ...prevReplies, [messageId]: data }));
        } catch (error) {
            console.error("Error fetching replies:", error);
        }
    };

    const toggleReplies = (messageId) => {
        if (visibleReplies === messageId) {
            setVisibleReplies(null);
        } else {
            setVisibleReplies(messageId);
            if (!repliesData[messageId]) {
                fetchReplies(messageId);
            }
        }
    };

    const handleReplyInputChange = (messageId, e) => {
        setReplyInputData({ ...replyInputData, [messageId]: e.target.value });
    };


    const memoizedMessages = React.useMemo(() => uniqBy(messages, '_id'), [messages]);
    const arrangedMessages = memoizedMessages.reverse()

    return (
        <>
            {addDocModal && (
                <UploadDocModal
                    close={() => setAddDocModal(false)}
                    uploadFunc={handleUpload}
                    fileId={inputId}
                    loading={false}
                />
            )}
            <div className="relative flex flex-col gap-4 p-6 bg-white rounded-lg">
                <div className="flex items-start gap-2 rounded-lg">
                    <div className="flex items-center justify-center w-10 h-10 font-semibold text-white bg-gray-300 rounded-full">
                        {getInitialsMessage(businessName)}
                    </div>

                    <div className="flex-1">
                        <InputSection
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onSend={sendMessageFunc}
                            inputId="main"
                            uploadedFile={fileData?.["main"]}
                            onFileChange={(id, event) => {
                                setInputId(id);
                                setAddDocModal(true);
                            }}
                            onFileRemove={handleFileRemove}
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-6">
                    {loading ? (
                        <Loader />
                    ) : arrangedMessages.length > 0 ? (
                        arrangedMessages?.map((msg, index, reversedMessages) => (
                            <div
                                key={msg?._id + index}
                                className={`flex flex-col gap-2 p-2 pb-4 rounded-lg ${
                                    index !== reversedMessages.length - 1 ? "border-b" : ""
                                }`}
                            >
                                <div className="flex gap-4">
                                    <div className="flex items-center justify-center w-10 h-10 font-semibold text-white bg-gray-300 rounded-full">
                                        {getInitialsMessage(msg?.senderId?.name)}
                                    </div>
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-semibold text-gray-700">
                                                {msg?.senderId?.name || "Unknown"}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {formatDate(new Date(msg?.created_at))}
                                            </div>
                                        </div>
                                        <div className="mt-1 mb-2 text-sm text-gray-700">
                                            <p className="pb-3">{msg?.message}</p>
                                            {msg?.attachments &&
                                                msg?.attachments.map((attachment) => (
                                                    <a
                                                        key={attachment?._id}
                                                        href={attachment?.attachmentUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-2 py-1 mt-2 text-blue-500 underline border rounded-lg"
                                                    >
                                                        {attachment?.attachmentName ||
                                                            "Download Attachment"}
                                                    </a>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between gap-4 pl-16">
                                    {msg?.replies && msg?.replies.length > 0 && (
                                        <div
                                            className="text-[#667085] cursor-pointer flex items-center gap-1"
                                            onClick={() => toggleReplies(msg?._id)}
                                        >
                                            {msg?.replies.length}{" "}
                                            {msg?.replies.length > 1 ? "Replies" : "Reply"}
                                            {visibleReplies === msg?._id ? (
                                                <FaChevronUp size={12} />
                                            ) : (
                                                <FaChevronDown size={12} />
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-1">
                                        <div
                                            className="text-[#1D2939] cursor-pointer"
                                            onClick={() => toggleReplies(msg?._id)}
                                        >
                                            Reply
                                        </div>
                                        <LuDot />
                                        <div
                                            className="text-[#1D2939] cursor-pointer"
                                            onClick={() => {
                                                setInputId(msg?._id);
                                                setVisibleReplies(msg?._id);
                                                setAddDocModal(true);
                                            }}
                                        >
                                            Attach
                                        </div>
                                    </div>
                                </div>

                                {/* Replies Section */}
                                {visibleReplies === msg._id && (
                                    <div className="relative flex flex-col gap-4 pt-2 mt-2">
                                        {/* Vertical Line */}
                                        {repliesData[msg._id]?.length > 0 && (
                                            <div className="absolute left-4 top-[-110px] bottom-48 w-px bg-gray-300"></div>
                                        )}
                                        {repliesData[msg._id]?.map((reply) => (
                                            <div
                                                key={reply?._id}
                                                className="relative flex items-start gap-4"
                                            >
                                                <div className="flex items-center justify-center w-8 h-8 font-semibold text-white bg-gray-200 rounded-full">
                                                    {getInitialsMessage(reply?.senderId?.name)}
                                                </div>
                                                <div className="flex flex-col">
                                                    <div>
                                                        <span className="text-xs font-semibold text-gray-700">
                                                            {reply?.senderId.fullName ||
                                                                reply?.senderId.name ||
                                                                "Unknown"}
                                                        </span>
                                                        <span className="pl-3 text-xs text-gray-500">
                                                            {formatDate(
                                                                new Date(reply?.created_at)
                                                            )}
                                                        </span>
                                                    </div>
                                                    <p className="py-2 text-sm text-gray-700">
                                                        {reply?.message}
                                                    </p>
                                                    {reply.attachments &&
                                                        reply.attachments.map(
                                                            (attachment, index) => (
                                                                <a
                                                                    key={index + attachment?._id}
                                                                    href={attachment.attachmentUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="px-2 py-1 mt-2 text-blue-500 underline border rounded-lg"
                                                                >
                                                                    {attachment?.attachmentName ||
                                                                        "Download Attachment"}
                                                                </a>
                                                            )
                                                        )}
                                                </div>
                                            </div>
                                        ))}
                                        {/* Input Section for Reply */}
                                        <div className="flex items-start gap-2 rounded-lg">
                                            <div className="flex items-center justify-center w-10 h-10 font-semibold text-white bg-gray-300 rounded-full">
                                                {getInitialsMessage(businessName)}
                                            </div>
                                            <div className="flex-1">
                                                <InputSection
                                                    value={replyInputData[msg?._id] || ""}
                                                    onChange={(e) =>
                                                        handleReplyInputChange(msg?._id, e)
                                                    }
                                                    onSend={() => sendMessageFunc(msg?._id)}
                                                    inputId={msg?._id}
                                                    uploadedFile={fileData[msg?._id]}
                                                    onFileRemove={handleFileRemove}
                                                    // uploadedFile={fileData["main"]}
                                                    onFileChange={(id, event) => {
                                                        setInputId(id);
                                                        setAddDocModal(true);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center mt-4 text-center text-gray-500">
                            <NoMessages />
                            <p>No messages here</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Messages;
